import React from 'react'
import { useFormikContext } from 'formik'
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RateReviewIcon from '@mui/icons-material/RateReview'
import VerifiedIcon from '@mui/icons-material/Verified'
import { useAuth } from '../../../context/AuthProvider'

export const REGISTRATION_STATUS = {
  NO_VERIFICADO: 'no_verificado',
  EN_REVISION: 'en_revision',
  VERIFICADO: 'verificado',
}

export const REGISTRATION_STATUS_LABELS = {
  [REGISTRATION_STATUS.VERIFICADO]: 'Verificado',
  [REGISTRATION_STATUS.EN_REVISION]: 'En revisión',
  [REGISTRATION_STATUS.NO_VERIFICADO]: 'No verificado',
}

export const STATUS_META = {
  [REGISTRATION_STATUS.VERIFICADO]: {
    icon: VerifiedIcon,
    color: 'success.main',
    borderColor: 'success.main',
    background: 'rgba(46, 125, 50, 0.08)',
  },
  [REGISTRATION_STATUS.EN_REVISION]: {
    icon: RateReviewIcon,
    color: 'warning.main',
    borderColor: 'warning.main',
    background: 'rgba(237, 108, 2, 0.10)',
  },
  [REGISTRATION_STATUS.NO_VERIFICADO]: {
    icon: ErrorOutlineIcon,
    color: 'error.main',
    borderColor: 'error.main',
    background: 'rgba(211, 47, 47, 0.08)',
  },
}

export const normalizeRegistrationStatus = (value, treeVerified) => {
  if (Object.values(REGISTRATION_STATUS).includes(value)) return value
  return treeVerified === 1 || treeVerified === '1' || treeVerified === true
    ? REGISTRATION_STATUS.VERIFICADO
    : REGISTRATION_STATUS.NO_VERIFICADO
}

export const cleanRevisionObservations = value => {
  if (!Array.isArray(value)) return []
  return value
    .map(item => ({
      ...item,
      texto: String(item?.texto ?? '').trim(),
    }))
    .filter(item => item.texto)
}

export const hasRevisionObservations = value => cleanRevisionObservations(value).length > 0

const createObservation = user => ({
  id: `review-${Date.now()}`,
  texto: '',
  createdAt: new Date().toISOString(),
  createdBy: user?.id || null,
})

const formatCorreccionFecha = value => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' })
}

const RegistrationStatusPanel = ({ container = 'panel' }) => {
  const { values, setFieldValue, errors, touched } = useFormikContext()
  const { isAdmin, user } = useAuth()
  const status = normalizeRegistrationStatus(values.estadoRegistro, values.treeVerified_)
  const meta = STATUS_META[status] || STATUS_META[REGISTRATION_STATUS.NO_VERIFICADO]
  const Icon = meta.icon
  const observations = Array.isArray(values.observacionesRevision)
    ? values.observacionesRevision
    : []
  const editable = Boolean(isAdmin)
  const showObservationError =
    status === REGISTRATION_STATUS.EN_REVISION &&
    touched.observacionesRevision &&
    errors.observacionesRevision

  const handleStatusChange = nextStatus => {
    if (!editable) return
    setFieldValue('estadoRegistro', nextStatus)
    setFieldValue('treeVerified_', nextStatus === REGISTRATION_STATUS.VERIFICADO ? 1 : 0)

    if (nextStatus === REGISTRATION_STATUS.EN_REVISION) {
      if (!observations.length) {
        setFieldValue('observacionesRevision', [createObservation(user)])
      }
      return
    }

    setFieldValue('observacionesRevision', [])
  }

  const handleObservationChange = (index, texto) => {
    const next = observations.map((item, itemIndex) =>
      itemIndex === index ? { ...item, texto } : item,
    )
    setFieldValue('observacionesRevision', next)
  }

  const handleAddObservation = () => {
    setFieldValue('observacionesRevision', [...observations, createObservation(user)])
  }

  const handleDeleteObservation = index => {
    const next = observations.filter((_, itemIndex) => itemIndex !== index)
    setFieldValue('observacionesRevision', next.length ? next : [createObservation(user)])
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: meta.borderColor,
        bgcolor: meta.background,
        borderRadius: container === 'card' ? 2 : 0,
        px: { xs: 1.5, md: 2 },
        py: 1.5,
        mb: container === 'panel' ? 0 : 2,
      }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon sx={{ fontSize: 22, color: meta.color }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Estado de registro
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {REGISTRATION_STATUS_LABELS[status]}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {Object.values(REGISTRATION_STATUS).map(option => (
            <Chip
              key={option}
              label={REGISTRATION_STATUS_LABELS[option]}
              size="small"
              color={
                option === REGISTRATION_STATUS.VERIFICADO
                  ? 'success'
                  : option === REGISTRATION_STATUS.EN_REVISION
                    ? 'warning'
                    : 'error'
              }
              variant={status === option ? 'filled' : 'outlined'}
              onClick={editable ? () => handleStatusChange(option) : undefined}
              sx={{
                fontWeight: 700,
                cursor: editable ? 'pointer' : 'default',
                opacity: editable || status === option ? 1 : 0.62,
              }}
            />
          ))}
        </Stack>
      </Stack>

      {status === REGISTRATION_STATUS.EN_REVISION && (
        <Box sx={{ mt: 1.5 }}>
          <Stack spacing={1}>
            {editable ? (
              observations.map((item, index) => (
                <Stack key={item.id || index} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label={`Corrección ${index + 1}`}
                    value={item.texto || ''}
                    onChange={event => handleObservationChange(index, event.target.value)}
                    error={Boolean(showObservationError) && !String(item.texto || '').trim()}
                  />
                  <IconButton
                    type="button"
                    aria-label="Eliminar corrección"
                    color="error"
                    size="small"
                    onClick={() => handleDeleteObservation(index)}
                    sx={{ mt: 0.5 }}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))
            ) : (
              cleanRevisionObservations(observations).map((item, index) => (
                <Typography key={item.id || index} variant="body2">
                  {index + 1}. {item.texto}
                </Typography>
              ))
            )}
          </Stack>

          {showObservationError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
              {Array.isArray(errors.observacionesRevision)
                ? 'Revise las correcciones ingresadas.'
                : errors.observacionesRevision}
            </Typography>
          )}

          {values.correccionAplicadaAt && (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                borderRadius: 1,
                bgcolor: 'rgba(46, 125, 50, 0.08)',
                border: '1px dashed',
                borderColor: 'success.main',
              }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <VerifiedIcon sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">
                  Corrección aplicada por el personal de campo
                  {values.correccionAplicadaPorNombre ? ` (${values.correccionAplicadaPorNombre})` : ''}
                  {` el ${formatCorreccionFecha(values.correccionAplicadaAt)}`}. Revise y verifique para cerrarla.
                </Typography>
              </Stack>
            </Box>
          )}

          {editable && (
            <Button
              type="button"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddObservation}
              sx={{ mt: 1 }}>
              Agregar corrección
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

export default RegistrationStatusPanel
